import { LiveViewSessionOptions, LiveViewStage, startLiveViewSession } from './liveViewAPIs';

// Live view reporting shared by every student tool. A tool only decides when
// reporting starts and maps its own state to answered prompts (the per-tool
// functions below); this owns the rest: the session, reporting each prompt
// once, and stage/idle changes.

export interface AnsweredPrompt {
  id: string,
  optimal: boolean,
  // The question's position, so the teacher's view orders out-of-order reports
  index?: number,
}

export interface LiveViewProgress {
  // Reports every answered prompt not yet reported, then any stage or idle change
  sync: (answered: AnsweredPrompt[]) => void,
  // Sends a stage or idle change straight away, for tools that track those
  // separately from their answers
  reportState: () => void,
  stop: () => void,
}

// Time on the current question for tools that time each question under a
// prompt_N step; null on title cards and other non-question steps
export const promptStepTimeMs = (step: string|undefined, timeTracking: { [key: string]: number }|null): number|null => (
  step && step.startsWith('prompt_') ? ((timeTracking || {})[step] || 0) : null
);

export const createLiveViewProgress = (sessionUid: string, options: LiveViewSessionOptions): LiveViewProgress => {
  const session = startLiveViewSession(sessionUid, options);

  return {
    sync: (answered) => {
      answered.forEach((prompt) => {
        if (!session.hasReported(prompt.id)) { session.reportPrompt(prompt.id, prompt.optimal, prompt.index); }
      });
      session.reportState();
    },
    reportState: () => session.reportState(),
    stop: () => session.stop(),
  };
};

// The question-flow tools (Connect, Diagnostic, Grammar) time their start page
// (and any language choice) under 'landing', as they do the finished screen
export const questionFlowStage = (step: string, answeredQuestions: unknown[]|undefined): LiveViewStage => (
  step === 'landing' && !(answeredQuestions || []).length ? 'directions' : 'prompts'
);

const TITLE_CARD_TYPE = 'TL';

function lastOf<T>(items: T[]|undefined): T|undefined { return items && items.length ? items[items.length - 1] : undefined; }

// Attempts are saved as { response }, except in older Grammar sessions, which
// saved the bare response
const attemptResponse = (attempt) => (attempt && 'response' in attempt ? attempt.response : attempt);

// The question-flow tools (Connect, Diagnostic, Grammar) keep a list of
// answered questions whose final attempt says whether the answer was optimal;
// they differ only in where each entry keeps the question and its id
const promptsFromAnsweredQuestions = (answeredQuestions: any[]|undefined, questionOf: (entry) => any, idOf: (question) => string): AnsweredPrompt[] => (
  (answeredQuestions || []).flatMap((entry, index) => {
    const question = questionOf(entry);
    if (entry.type === TITLE_CARD_TYPE || !question || !(question.attempts || []).length) { return []; }

    const response = attemptResponse(lastOf(question.attempts));
    return [{ id: idOf(question), optimal: !!(response && response.optimal), index }];
  })
);

export const connectAnsweredPrompts = (answeredQuestions: any[]): AnsweredPrompt[] => (
  promptsFromAnsweredQuestions(answeredQuestions, entry => entry.question, question => question.key)
);

export const diagnosticAnsweredPrompts = (answeredQuestions: any[]): AnsweredPrompt[] => (
  promptsFromAnsweredQuestions(answeredQuestions, entry => entry.data || entry.question, question => question.key)
);

export const grammarAnsweredPrompts = (answeredQuestions: any[]): AnsweredPrompt[] => (
  promptsFromAnsweredQuestions(answeredQuestions, entry => entry, question => question.uid || question.key)
);

// Evidence keeps every response per prompt; a prompt is done once a response
// is optimal or the attempts run out
export const evidenceAnsweredPrompts = (submittedResponses: { [promptId: string]: { optimal?: boolean }[] }, maxAttempts: number): AnsweredPrompt[] => (
  Object.keys(submittedResponses || {}).flatMap((promptId) => {
    const responses = submittedResponses[promptId] || [];
    const last = lastOf(responses);
    if (!last || !(last.optimal || responses.length >= maxAttempts)) { return []; }

    return [{ id: String(promptId), optimal: !!last.optimal }];
  })
);
